const axios = require('axios')
const raw = require('nanohtml/raw')
const html = require('nanohtml')
import { createPopper } from '@popperjs/core'
import tourConfig from './tour-config'
import Rails from '@rails/ujs'
import Cookies from 'js-cookie'

class Tour {
  trigger = document.querySelector('.js-trigger-tour')
  triggerSegment = this.trigger.getAttribute('data-tour-segment')
  allSteps = []
  activeConfig = null
  step = this.getCurrentStep()
  status = this.getStatus()
  segment = this.getSegment()
  tourConfig = tourConfig

  constructor() {
    this.setupTour()

    // Move the continue tour button as sidebar is toggled
    document.addEventListener('sidebar:toggle', event => {
      if (this.status === 'cancelled') {
        const continueEl = document.querySelector('.tour-continue')

        if (!continueEl) return

        if (event.detail) {
          continueEl.classList.add('tour-continue--sidebar')
        } else {
          continueEl.classList.remove('tour-continue--sidebar')
        }
      }
    })

    // Progress the tour if user selects HTML element rather than Next button
    const nextSegmentLink = document.querySelector('.js-tour-next-segment')
    if (nextSegmentLink) {
      nextSegmentLink.addEventListener('click', event => {
        if (this.status === 'inprogress') {
          event.preventDefault()
          this.setStep(0)
          this.setSegment(nextSegmentLink.getAttribute('data-segment'))
          window.location.href = nextSegmentLink.getAttribute('href')
        }
      })
    }

    // Reload the tour if the chart fails to load
    document.addEventListener('chart:error', this.setupTour.bind(this))

    // Update curtain size on resize
    window.addEventListener('resize', this.onWindowResize.bind(this))

    // Show investigate step when modal opens
    document.addEventListener(
      'modal.tour.investigate',
      this.onInvestigateShow.bind(this)
    )
  }

  setupTour() {
    this.clean()
    this.getAllSteps()
    this.activeConfig = this.tourConfig[this.segment].steps[this.step]

    if (this.status === 'cancelled') {
      this.showContinue()
    } else if (this.status === 'inprogress') {
      if (this.onTourURL()) {
        this.startTour()
      } else {
        // User ended up on another example page that isn't part of the tour
        this.setStatus('cancelled')
        this.showContinue()
      }
    }
  }

  onLastStepOfSegment() {
    return this.step === this.tourConfig[this.segment].steps.length - 1
  }

  showContinue() {
    const stateClass = document
      .querySelector('.sidebar')
      .classList.contains('sidebar--open')
      ? 'tour-continue--sidebar'
      : ''
    const handler = this.activeConfig.continue
      ? new Function(`${this.activeConfig.continue}()`).bind(this)
      : this.onContinue.bind(this)
    const el = html` <button
      class="tour-continue ${stateClass}"
      onclick=${handler}
    >
      <div class="tour-continue_icon"></div>
      <div class="tour-continue_text">
        <h4>Continue Tour</h4>
        <p>
          Step ${this.getCurrentTotalStep(this.activeConfig)} of
          ${this.allSteps.length}
        </p>
      </div>
    </button>`

    document.body.appendChild(el)
  }

  onContinue() {
    document.querySelector('.tour-continue').remove()
    this.setStatus('inprogress')

    if (this.triggerSegment === this.segment) {
      // If user is on the same page as the current segment
      this.startTour()
    } else {
      // Otherwise relocate to the page and trigger the tour
      window.location.href = this.tourConfig[this.segment].url
    }
  }

  startTour() {
    if (Cookies.get('tour-investigate-step')) {
      this.continueInvestigate(true)
    } else {
      this.showPopover(this.tourConfig[this.segment].steps[this.step])
    }
  }

  onWindowResize() {
    if (document.querySelector('.tour-curtain')) {
      this.clipCurtain(this.activeConfig.selector)
    }
  }

  showPopover(config) {
    const { selector, position = 'top', hideCurtain, popperMods = [] } = config

    this.activeConfig = config
    this.clean()
    this.notifyApp()

    const reference = document.querySelector(selector)

    // If reference selector is not found, show continue button
    if (!reference) {
      this.setStatus('cancelled')
      return this.showContinue()
    }

    // Show popover & curtain
    document.body.appendChild(this.createPopover(config))

    const popover = document.querySelector('.js-tour-popover')

    // Position the popover
    createPopper(reference, popover, {
      placement: position,
      modifiers: [
        { name: 'flip', enabled: false },
        { name: 'offset', options: { offset: [0, 25] } },
        ...popperMods,
      ],
    })

    if (!hideCurtain) {
      if (!document.querySelector('.tour-curtain')) {
        document.body.appendChild(this.createCurtain())
      }
      this.clipCurtain(selector)
    }

    this.scrollToElement(popover, reference)
  }

  scrollToElement(popover, reference) {
    // Check if element is visible
    function isScrolledIntoView(el) {
      const rect = el.getBoundingClientRect()
      return rect.top >= 0 && rect.bottom <= window.innerHeight
    }

    setTimeout(() => {
      const element =
        this.activeConfig.position === 'bottom' ? reference : popover
      const elementTop =
        element.getBoundingClientRect().top + window.scrollY - 30

      // Only scroll if elements are not visible
      if (!isScrolledIntoView(element) || !isScrolledIntoView(reference)) {
        window.scroll({
          top: elementTop,
          left: 0,
          behavior: 'smooth',
        })
      }
    }, 25)
  }

  createPopover(config) {
    const {
      description,
      position = 'top',
      hideArrow = false,
      next = {},
      prev = {},
    } = config
    return html` <div class="tour-popover js-tour-popover" aria-modal="true">
      <div class="tour-popover_content">${raw(description)}</div>
      <button
        class="tour-popover_close"
        aria-label="close"
        onclick=${this.cancelTour.bind(this)}
      >
        ✕
      </button>
      <div class="tour-popover_nav">
        <p class="tour-popover_steps">
          ${raw(this.getCurrentTotalStep(config))} of
          ${raw(this.allSteps.length)}
        </p>
        <div>
          ${this.getCurrentTotalStep(config) > 1
            ? this.createNavButton(false, prev)
            : null}
          ${this.createNavButton(true, next)}
        </div>
      </div>
      ${raw(
        !hideArrow
          ? `<div class="tour-popover_arrow tour-popover_arrow--${position}" data-popper-arrow></div>`
          : null
      )}
    </div>`
  }

  createNavButton(isNext, nav) {
    const { action, label, toSegment } = nav
    const classMod = isNext ? 'shadow' : 'secondary'
    const defaultLabel = isNext ? '→' : '←'
    const ariaLabel = isNext ? 'Next' : 'Previous'
    const handler = action
      ? new Function(`${action}()`).bind(this)
      : this.onNavigate.bind(this, event, isNext, toSegment)

    return html` <button
      class="btn btn--${classMod} tour-popover_nav-btn"
      aria-label="${label || ariaLabel}"
      title="${label || ariaLabel}"
      onclick=${handler}
    >
      ${label || defaultLabel}
    </button>`
  }

  onNavigate(event, isNext, segment) {
    this.setStatus('inprogress')

    if (segment) {
      // Navigate to new dest page
      const upcomingSegment = this.tourConfig[segment]
      const upcomingStep = isNext ? 0 : upcomingSegment.steps.length - 1
      this.setStep(upcomingStep)
      this.setSegment(segment)
      window.location.href = upcomingSegment.url
    } else {
      // Show step on the same page
      this.setStep(isNext ? this.step + 1 : this.step - 1)
      this.showPopover(this.tourConfig[this.segment].steps[this.step])
    }
  }

  cancelTour() {
    if (this.getCurrentTotalStep(this.activeConfig) === this.allSteps.length) {
      this.endTour()
    } else {
      this.setStatus('cancelled')
      this.closeTour()
      this.notifyApp()
      this.showContinue()
    }
  }

  endTour() {
    this.setStatus('ended')
    this.closeTour()
    this.notifyApp()
  }

  clean() {
    const popover = document.querySelector('.js-tour-popover')
    const curtain = document.querySelector('.tour-curtain')
    const continueButton = document.querySelector('.tour-continue')

    if (popover) {
      popover.remove()
    }
    if (curtain) {
      curtain.remove()
    }
    if (continueButton) {
      continueButton.remove()
    }
  }

  closeTour() {
    this.clean()
    window.removeEventListener('resize', this.onWindowResize)
  }

  notifyApp() {
    if (!Cookies.get('tour-disable-requests')) {
      axios
        .patch(this.trigger.getAttribute('data-url'), {
          step: this.getCurrentTotalStep(this.activeConfig),
          status: this.status,
          authenticity_token: Rails.csrfToken(),
        })
        .then(() => {
          if (this.status === 'ended') {
            Cookies.set('tour-disable-requests', true)
          }
        })
    }
  }

  createCurtain() {
    return html`<div class="tour-curtain"></div>`
  }

  clipCurtain(target) {
    const el = document.querySelector('.tour-curtain')
    const pageContent = document.querySelector('.page_content')
    const targetEl = document.querySelector(target)
    const targetPos = targetEl.getBoundingClientRect()
    const padding = this.getPadding(targetEl)
    const config = {
      top: targetPos.top + padding.top + window.scrollY,
      right: targetPos.right - padding.right,
      bottom: targetPos.bottom + padding.bottom + window.scrollY,
      left: targetPos.left + padding.left,
      contentHeight: pageContent.clientHeight + 20,
      offset: 10,
    }

    el.style.cssText = `clip-path: polygon(${this.clippingPath(config)});
                        height: ${config.contentHeight + config.offset}px;`
  }

  clippingPath(config) {
    const { top, right, bottom, left, contentHeight, offset } = config
    return `0 0,
            0 ${contentHeight + offset}px,
            ${left - offset}px ${contentHeight + offset}px,
            ${left - offset}px ${top - offset + 3}px,
            ${left - offset + 1}px ${top - offset + 2}px,
            ${left - offset + 2}px ${top - offset + 1}px,
            ${left - offset + 3}px ${top - offset + 0}px,
            ${right + offset}px ${top - offset}px,
            ${right + offset - 3}px ${top - offset}px,
            ${right + offset - 2}px ${top - offset + 1}px,
            ${right + offset - 1}px ${top - offset + 2}px,
            ${right + offset - 0}px ${top - offset + 3}px,
            ${right + offset}px ${bottom + offset - 3}px,
            ${right + offset - 1}px ${bottom + offset - 2}px,
            ${right + offset - 2}px ${bottom + offset - 1}px,
            ${right + offset - 3}px ${bottom + offset - 0}px,
            ${left - offset + 3}px ${bottom + offset}px,
            ${left - offset + 2}px ${bottom + offset - 1}px,
            ${left - offset + 1}px ${bottom + offset - 2}px,
            ${left - offset + 0}px ${bottom + offset - 3}px,
            ${left - offset}px 110%,
            100% 100%,
            100% 0%`
  }

  getPadding(target) {
    const styles = window.getComputedStyle(target, null)

    function convertPixels(styles, property) {
      return parseInt(styles.getPropertyValue(property).replace('px', ''))
    }

    return {
      top: convertPixels(styles, 'padding-top'),
      right: convertPixels(styles, 'padding-right'),
      bottom: convertPixels(styles, 'padding-bottom'),
      left: convertPixels(styles, 'padding-left'),
    }
  }

  /**
   * State getters/setters
   * Use local storage for knowing the current state of the tour
   */

  getSegment() {
    const storageSegment = Cookies.get('tour_segment')
    const pageSegment = this.triggerSegment

    if (typeof storageSegment === 'undefined') {
      const segment = pageSegment || 'domain'
      this.setSegment(segment)
      return segment
    } else {
      return storageSegment
    }
  }

  setSegment(segment) {
    Cookies.set('tour_segment', segment, { expires: 3650 })
    this.segment = segment
  }

  getStatus() {
    const status = Cookies.get('tour_status')

    if (status === 'cancelled') {
      return 'cancelled'
    } else if (status === 'ended') {
      return 'ended'
    } else {
      this.setStatus('inprogress')
      return 'inprogress'
    }
  }

  setStatus(status, callback) {
    Cookies.set('tour_status', status, { expires: 3650 })

    if (this.status !== 'ended') {
      this.status = status
    }

    if (callback) callback()
  }

  getCurrentStep() {
    const step = Cookies.get('tour_step')

    if (typeof step === 'undefined') {
      this.setStep(0)
      return 0
    } else {
      return JSON.parse(step)
    }
  }

  setStep(step) {
    Cookies.set('tour_step', step, { expires: 3650 })
    this.step = step
  }

  getAllSteps() {
    this.allSteps = []
    for (const segment in this.tourConfig) {
      if (Object.prototype.hasOwnProperty.call(this.tourConfig, segment)) {
        this.allSteps = this.allSteps.concat(this.tourConfig[segment].steps)
      }
    }
  }

  getCurrentTotalStep(config) {
    return (
      this.allSteps.findIndex(x => x.description === config.description) + 1
    )
  }

  onTourURL() {
    return (
      window.location.pathname + location.search ===
      this.tourConfig[this.segment].url
    )
  }

  /**
   * Special tour rules for the investigate modal
   */

  showInvestigate() {
    this.clean()
    this.setStep(this.step + 1)
    this.triggerInvestigateShow()
  }

  endPrev() {
    this.clean()
    this.setStep(this.step - 1)
    this.triggerInvestigateShow()
  }

  continueInvestigate(onLoad) {
    const url = this.tourConfig['source-ip'].url

    // Check if we're on the same page
    if (window.location.pathname + location.search === url) {
      this.setStatus('inprogress', () => {
        this.triggerInvestigateShow(onLoad)
      })
    } else {
      // Redirect to the source IP page
      Cookies.set('tour-investigate-step', true, { expires: 3650 })
      this.setStatus('inprogress')
      window.location.href = url
    }
  }

  triggerInvestigateShow(onLoad) {
    const el = document.querySelector(
      'a[data-modal-event="modal.tour.investigate"]'
    )
    this.triggerInvestigateClick = true

    if (onLoad) {
      // When triggering the investigate modal onLoad we need to delay the click event
      // in order to make sure everything is loaded properly
      setTimeout(() => {
        el.click()
      }, 200)
    } else {
      el.click()
    }
  }

  onInvestigateShow() {
    if (this.status === 'inprogress') {
      if (this.triggerInvestigateClick) {
        // User selected paging buttons
        this.triggerInvestigateClick = false
      } else {
        if (this.step === 4 || this.step === 5) {
          // User selected element on 4th step
          // Or the tour was cancelled and they are continuing
          this.setStep(5)
        } else {
          // Cancel the tour since the user selected element when we didn't ask them to
          this.setStatus('cancelled')
          this.clean()
          this.showContinue()
        }
      }

      document.addEventListener('ajax:complete', () => {
        // Only show the popover if we're in progress
        // Prevents the popover from showing if the investigate element was selected when we didn't expect it
        if (this.status === 'inprogress') {
          this.showPopover(this.tourConfig[this.segment].steps[this.step])

          // If someone closes the investigate modal
          document.addEventListener('modal:on:hide', () => {
            this.setStatus('cancelled')
            this.clean()
            this.showContinue()
          })
        }
      })
    }
  }

  investigateNext() {
    const modal = document.querySelector('.modal')
    modal.classList.remove('modal--visible')
    Cookies.remove('tour-investigate-step')

    this.setStep(this.step + 1)
    this.showPopover(this.tourConfig[this.segment].steps[this.step])
  }

  investigatePrev() {
    // Manually hide the modal since dispatching the event will show the final step
    const modal = document.querySelector('.modal')
    modal.classList.remove('modal--visible')
    Cookies.remove('tour-investigate-step')

    this.setStep(this.step - 1)
    this.showPopover(this.tourConfig[this.segment].steps[this.step])
  }
}

document.addEventListener('turbolinks:load', () => {
  if (document.querySelector('.js-trigger-tour')) {
    new Tour()
  }

  const closeTour = document.querySelector('.js-tour-close-callout')

  if (closeTour) {
    closeTour.addEventListener('click', () => {
      const callout = document.querySelector('.tour-callout')
      callout.classList.add('out')
      axios.patch(callout.getAttribute('data-url'), {
        status: 'discarded',
        authenticity_token: Rails.csrfToken(),
      })
    })
  }

  const continueTour = document.querySelector('.js-tour-continue-callout')

  if (continueTour) {
    continueTour.addEventListener('click', () => {
      Cookies.set('tour_status', 'inprogress', { expires: 3650 })
      window.location.href =
        tourConfig[continueTour.getAttribute('data-segment')].url
    })
  }
})
